import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getRandomKeyTakeaways } from '../../services/keyTakeawaysService';

/**
 * KeyTakeawaysSection Component
 * Displays random key takeaways from lectures
 */
const KeyTakeawaysSection = ({ googleSheetId, theme, isMobile }) => {
  const [takeaways, setTakeaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollContainerRef = React.useRef(null);

  useEffect(() => {
    const loadTakeaways = async () => {
      if (!googleSheetId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const randomTakeaways = await getRandomKeyTakeaways(googleSheetId, 3);
        setTakeaways(randomTakeaways);
      } catch (err) {
        console.error('Error loading key takeaways:', err);
        setError('Unable to load insights at this time');
      } finally {
        setLoading(false);
      }
    };

    loadTakeaways();
  }, [googleSheetId]);

  // Don't render if loading failed or no takeaways
  if (!loading && takeaways.length === 0) {
    return null;
  }

  // Loading state - hide section entirely to avoid showing empty cards with borders
  if (loading) {
    return null;
  }

  // Error state
  if (error) {
    return null; // Silently fail to not disrupt the page
  }

  return (
    <div style={{
      marginTop: '0',
      marginBottom: '64px'
    }}>
      {/* Section Header - Matching EventsSection style */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <h2 style={{
          fontSize: isMobile ? '20px' : '24px',
          fontWeight: '600',
          color: theme?.colors?.headingColor || theme?.colors?.primary || '#0f172a',
          margin: 0
        }}>
          Today's Insights
        </h2>
      </div>

      {/* Takeaways - Swipeable on mobile, Grid on desktop */}
      {isMobile ? (
        <div style={{
          position: 'relative'
        }}>
          {/* Swipeable Container */}
          <div 
            ref={scrollContainerRef}
            onScroll={(e) => {
              const container = e.target;
              const scrollLeft = container.scrollLeft;
              const containerWidth = container.offsetWidth;
              const cardWidth = containerWidth * 0.85;
              const newIndex = Math.round(scrollLeft / cardWidth);
              setActiveIndex(Math.min(Math.max(newIndex, 0), takeaways.length - 1));
            }}
            style={{
            display: 'flex',
            overflowX: 'auto',
            gap: '12px',
            padding: '4px',
            scrollSnapType: 'x mandatory',
            scrollBehavior: 'smooth',
            // Hide scrollbar
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: { display: 'none' }
          }}>
            {takeaways.map((takeaway, index) => (
              <div
                key={index}
                style={{
                  flex: '0 0 85%',
                  scrollSnapAlign: 'start'
                }}
              >
                <TakeawayCard
                  takeaway={takeaway}
                  theme={theme}
                  isMobile={isMobile}
                  index={index}
                  total={takeaways.length}
                />
              </div>
            ))}
          </div>
          
          {/* Swipe Hint and Dots */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            marginTop: '12px'
          }}>
            <div style={{
              display: 'flex',
              gap: '6px'
            }}>
              {takeaways.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '50%',
                    backgroundColor: theme?.colors?.primary || '#0f172a',
                    opacity: index === activeIndex ? 0.8 : 0.3,
                    transition: 'opacity 0.3s'
                  }}
                />
              ))}
            </div>
            <span style={{
              fontSize: '11px',
              color: theme?.colors?.textSecondary || '#94a3b8',
              fontStyle: 'italic'
            }}>
              Swipe for more →
            </span>
          </div>
        </div>
      ) : (
        // Desktop Grid
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '16px'
        }}>
          {takeaways.map((takeaway, index) => (
            <TakeawayCard
              key={index}
              takeaway={takeaway}
              theme={theme}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Individual Takeaway Card Component
 */
const TakeawayCard = ({ takeaway, theme, isMobile, index, total }) => {
  const [isHovered, setIsHovered] = useState(false);

  // Truncate text if too long - shorter on mobile
  const truncateText = (text, maxLength = 220) => {
    const limit = isMobile ? 180 : maxLength;
    if (text.length <= limit) return text;
    return text.substring(0, limit).trim() + '...';
  };

  // Format the lecture link
  const getLectureLink = () => {
    return `/lecture/${takeaway.lectureId}`;
  };

  return (
    <Link
      to={getLectureLink()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: isMobile ? '12px 14px' : '14px 16px',
        boxShadow: isHovered ? '0 2px 12px rgba(0,0,0,0.1)' : '0 1px 6px rgba(0,0,0,0.06)',
        border: '1px solid #e2e8f0',
        borderLeftWidth: '3px',
        borderLeftColor: theme?.colors?.primary || '#0f172a',
        transition: 'all 0.2s',
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'block',
        position: 'relative',
        transform: isHovered ? 'translateY(-1px)' : 'translateY(0)',
        minHeight: isMobile ? '120px' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      {/* Takeaway Text */}
      <div style={{
        marginBottom: '8px'
      }}>
        <p style={{
          color: theme?.colors?.primary || '#0f172a',
          fontSize: isMobile ? '15px' : '16px',
          lineHeight: '1.5',
          margin: 0,
          fontStyle: isMobile ? 'normal' : 'italic'
        }}>
          "{truncateText(takeaway.text)}"
        </p>
      </div>

      {/* View Lecture Link */}
      <div style={{
        fontSize: '13px',
        color: theme?.colors?.cardTextSecondary || '#64748b',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        <span>View full lecture</span>
        <span style={{ marginLeft: 'auto' }}>→</span>
      </div>
    </Link>
  );
};

export default KeyTakeawaysSection;